import { useEffect, useState } from 'preact/hooks';
import { route, navigate, back } from '@/state/nav';
import { animals, schedules, nowTick } from '@/state/data';
import { db } from '@/lib/db';
import { computeNextDue, timeShort, dueState } from '@/lib/due';
import { initials, avatarClass, type FeedLog, type Schedule } from '@/types';
import { Icon } from '@/components/Icon';
import { WeightChart } from '@/components/WeightChart';
import { ConfettiLeaves } from '@/components/ConfettiLeaves';
import { upsertAnimal } from '@/lib/mutations';
import { dialog, showToast, openSheet } from '@/state/ui';
import { ScheduleSheet } from '@/components/ScheduleSheet';

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  const t = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase();
  if (today) return t;
  return `${d.toLocaleDateString([], { weekday: 'short' })} ${t}`;
}

function scheduleLine(s: Schedule): { title: string; sub: string } {
  if (s.stype === 'interval') {
    const m = s.interval_minutes ?? 0;
    const title = m % 60 === 0 ? `Every ${m / 60} hour${m / 60 > 1 ? 's' : ''}` : `Every ${m} minutes`;
    const win =
      s.active_start && s.active_end
        ? `${s.active_start.slice(0, 5)}–${s.active_end.slice(0, 5)}`
        : 'all day';
    const amount = s.amount != null ? ` · ${s.amount} ${s.unit ?? ''} ${s.food ?? ''}`.trimEnd() : '';
    return { title, sub: `${win}${amount}` };
  }
  const times = (s.times ?? [])
    .map((t) => {
      const [h, mi] = t.split(':').map(Number);
      const ap = h >= 12 ? 'p' : 'a';
      const hh = h % 12 === 0 ? 12 : h % 12;
      return mi ? `${hh}:${String(mi).padStart(2, '0')}${ap}` : `${hh}${ap}`;
    })
    .join(' · ');
  const every = s.feed_every_n_days ? `Every ${s.feed_every_n_days} days` : 'Daily';
  const amount = s.amount != null ? ` · ${s.amount} ${s.unit ?? ''} ${s.food ?? ''}`.trimEnd() : '';
  return { title: every, sub: `${times}${amount}` };
}

export function RosterDetailScreen() {
  void nowTick.value;
  const id = route.value.params.id;
  const animal = animals.value.get(id);
  const [logs, setLogs] = useState<FeedLog[]>([]);
  const [confetti, setConfetti] = useState(false);

  useEffect(() => {
    void db.feed_logs
      .where('animal_id')
      .equals(id)
      .sortBy('fed_at')
      .then((ls) => setLogs(ls.reverse().slice(0, 30)));
  }, [id, nowTick.value]);

  if (!animal) {
    return (
      <div class="screen screen--no-tabbar">
        <header class="header header--sub">
          <button class="iconbtn" aria-label="Back" onClick={() => back('/roster')}>
            <Icon name="chevron-left" size={20} />
          </button>
        </header>
        <p class="roster-empty">Animal not found.</p>
      </div>
    );
  }

  const mySchedules = [...schedules.value.values()].filter((s) => s.animal_id === id && s.enabled);
  const primary = mySchedules[0];
  const nextDue = primary ? computeNextDue(primary) : null;
  const state = dueState(nextDue);
  const latestWeight = logs.find((l) => l.weight_g != null)?.weight_g;

  const release = () => {
    dialog.value = {
      title: `Release ${animal.name}?`,
      message: 'Marks this animal released and retires its schedules. The history stays.',
      confirmLabel: 'Release',
      onConfirm: async () => {
        await upsertAnimal(
          { name: animal.name, status: 'released', released_date: new Date().toISOString().slice(0, 10) },
          animal.id
        );
        setConfetti(true);
        setTimeout(() => setConfetti(false), 1600);
        showToast({ kind: 'success', message: `${animal.name} released — good luck out there 🌿` }, 4000);
      },
    };
  };

  const heroSub = [
    animal.species,
    animal.life_stage,
    latestWeight != null ? `${latestWeight}g` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div class="screen screen--no-tabbar screen--fade-in">
      {confetti && <ConfettiLeaves />}
      <header class="header header--sub">
        <button class="iconbtn" aria-label="Back" onClick={() => back('/roster')}>
          <Icon name="chevron-left" size={20} />
        </button>
        <div class="header__spacer" />
        {animal.status === 'active' && (
          <>
            <button class="iconbtn" aria-label="Release" onClick={release}>
              <Icon name="dove" size={20} />
            </button>
            <button class="iconbtn" aria-label="Edit" onClick={() => navigate(`/roster/${id}?edit=1`)}>
              <Icon name="edit" size={20} />
            </button>
          </>
        )}
      </header>

      <div class="scroll" style={{ padding: '0 20px 24px' }}>
        <div class="detail-hero">
          <span class={`avatar avatar--xl ${avatarClass(animal.category)}`}>{initials(animal.name)}</span>
          <div>
            <h1 class="t-h1">
              {animal.name}
              {animal.kind === 'batch' && <span class="feed-card__count" style={{ marginLeft: '8px', verticalAlign: 'middle' }}>×{animal.count}</span>}
            </h1>
            {heroSub && <p class="t-small sub">{heroSub}</p>}
            <div class="chips">
              {animal.status === 'released' ? (
                <span class="pill pill--gold">Released</span>
              ) : (
                <>
                  <span class={`pill ${animal.condition === 'critical' ? 'pill--danger' : animal.condition === 'watch' ? 'pill--warn' : 'pill--accent'}`}>
                    {animal.condition === 'stable' ? 'Stable' : animal.condition === 'watch' ? 'Watch' : 'Critical'}
                  </span>
                  {nextDue && (
                    <span class={`pill ${state === 'overdue' ? 'pill--danger' : 'pill--info'}`}>
                      {state === 'overdue' ? 'Late — due ' : 'Due '}
                      {timeShort(nextDue)}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {animal.diet && (
          <>
            <p class="t-eyebrow" style={{ margin: '6px 0 8px' }}>Diet</p>
            <div class="card" style={{ padding: '14px' }}>
              <b style={{ fontSize: 'var(--text-base)' }}>{animal.diet}</b>
              {animal.diet_notes && <p class="t-meta" style={{ margin: '4px 0 0' }}>{animal.diet_notes}</p>}
            </div>
          </>
        )}

        <WeightChartSection logs={logs} />

        <p class="t-eyebrow" style={{ margin: '18px 0 8px' }}>Schedule</p>
        {mySchedules.length === 0 && (
          <div class="card sched-card">
            <div>
              <b>No feeding schedule</b>
              <p class="t-meta">Add one so Tend can run the clock.</p>
            </div>
          </div>
        )}
        {mySchedules.map((s) => {
          const line = scheduleLine(s);
          return (
            <div key={s.id} class="card sched-card" style={{ marginBottom: '8px' }}>
              <div>
                <b>{line.title}</b>
                <p class="t-meta">{line.sub}</p>
              </div>
              <button
                class="iconbtn"
                aria-label="Edit schedule"
                onClick={() => openSheet({ title: 'Edit schedule', content: <ScheduleSheet animalId={id} schedule={s} /> })}
              >
                <Icon name="chevron-right" size={18} />
              </button>
            </div>
          );
        })}
        {animal.status === 'active' && (
          <button
            class="btn btn--secondary btn--sm"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => openSheet({ title: 'New schedule', content: <ScheduleSheet animalId={id} /> })}
          >
            <Icon name="plus" size={16} /> Add schedule
          </button>
        )}

        {logs.length > 0 && (
          <>
            <p class="t-eyebrow" style={{ margin: '18px 0 8px' }}>Recent</p>
            <div class="tl">
              {logs.slice(0, 12).map((l) => (
                <div key={l.id} class={`tl-ev ${l.weight_g != null && !l.amount ? 'tl-ev--weight' : ''} ${l.skipped ? 'tl-ev--skip' : ''}`}>
                  <div class="tl-ev__title">
                    {fmtWhen(l.fed_at)} ·{' '}
                    {l.skipped
                      ? 'skipped'
                      : l.weight_g != null && l.amount == null
                        ? `weight ${l.weight_g}g`
                        : `fed${l.amount != null ? ` ${l.amount}${l.unit ?? ''}` : ''}`}
                  </div>
                  {(l.fed_by || l.notes) && (
                    <p class="tl-ev__meta">
                      {l.fed_by ? `by ${l.fed_by}` : ''}
                      {l.fed_by && l.notes ? ' · ' : ''}
                      {l.notes ? `"${l.notes}"` : ''}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {animal.notes && (
          <>
            <p class="t-eyebrow" style={{ margin: '18px 0 8px' }}>Notes</p>
            <div class="card" style={{ padding: '14px' }}>
              <p class="t-small" style={{ margin: 0 }}>{animal.notes}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function WeightChartSection({ logs }: { logs: FeedLog[] }) {
  const hasWeights = logs.filter((l) => l.weight_g != null).length >= 2;
  if (!hasWeights) return null;
  return (
    <>
      <p class="t-eyebrow" style={{ margin: '14px 0 8px' }}>Weight · last 7 days</p>
      <WeightChart logs={logs} />
    </>
  );
}
